import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { emitRouteChange } from "../utils/embedParentOrigin";

// Routes that must never be reported. Each one carries a credential in the URL
// and exists only to consume it and redirect, so restoring one on refresh would
// be both unsafe and pointless — the token is single-use and already spent.
//
//   /share/:token, /guest-share/:token   the token IS the path segment
//   /auth                                login, nothing to restore
const SKIP_PATH_PREFIXES = ["/share/", "/guest-share/", "/auth"];

// Query params allowed to leave this app and be written into office's address
// bar. An allowlist, deliberately: a denylist would leak whatever sensitive
// param gets added next. A param missing from here costs a filter that does not
// survive a refresh — cosmetic, not a credential.
// pages/embed/EmbedLiveEdit.jsx (`fileId`, `mode`) and the my-docs deep link
// (`folder`, `file` — pages/embed/EmbedMyDocs.jsx).
const SAFE_QUERY_PARAMS = ["fileId", "mode", "folder", "file"];

function safeSearch(search) {
    const params = new URLSearchParams(search);
    const safe = new URLSearchParams();

    for (const key of SAFE_QUERY_PARAMS) {
        const value = params.get(key);
        if (value !== null) safe.set(key, value);
    }

    const query = safe.toString();
    return query ? `?${query}` : "";
}

// Tells office which inner page we are on, so it can stash it in its URL hash
// and a refresh resumes here instead of on the module default. No-op when
// running standalone.
export function useEmitRouteChange() {
    const location = useLocation();
    const lastEmittedRef = useRef(null);

    useEffect(() => {
        if (window.parent === window) return;
        if (SKIP_PATH_PREFIXES.some((p) => location.pathname.startsWith(p))) return;

        const path = location.pathname + safeSearch(location.search);
        if (lastEmittedRef.current === path) return;
        lastEmittedRef.current = path;

        emitRouteChange(path);
    }, [location.pathname, location.search]);
}
